// ── Miniaturas de las imágenes de Cloud Storage ──────────────────────────────
//
// [165] El egreso de Cloud Storage es la mitad de la factura, y lo que lo mueve
// no es el tamaño del bucket sino cuántas veces se sirve cada foto: medido el
// 2026-08-28, ~44.600 lecturas de objeto al día a **166 KB de media**. Las
// fotos se guardan a 1920 px (que es lo correcto para verlas a pantalla
// completa) pero se pintan dentro de rejillas de 150-450 px: el álbum de
// JartiCards enseña 9 cromos por hoja y el feed social 3 tarjetas por fila.
//
// La solución es una SEGUNDA copia pequeña al lado de cada original.
//
// LA CLAVE DEL DISEÑO: la URL de la miniatura **se deriva de la del original**,
// así que NO hay que migrar ni un documento de Firestore. Para que eso funcione
// la miniatura se sube con **el mismo `firebaseStorageDownloadTokens`** que su
// original (el token es lo único que va en la URL pública), y su ruta es
// siempre `<ruta-sin-extensión>.thumb.webp` — siempre WebP, sin ambigüedad.
//
// Si la miniatura no existe no pasa nada: `Image` cae al original con `onError`.

/**
 * Carpetas de Storage que tienen miniatura. Fuera quedan a propósito:
 * `city_map/` (se calibra al píxel), `estampitas/` y `programa_ss_render/` (las
 * lee el mod con `NativeImage`, que no entiende WebP) y todo lo que no sea una
 * imagen. Ver `docs/costes.md`.
 */
export const CARPETAS_CON_MINIATURA = ['social', 'jarticards', 'packs', 'periodico'] as const;

/** Lado mayor de la miniatura, en píxeles. */
export const MINIATURA_LADO = 600;
/** Calidad WebP de la miniatura. */
export const MINIATURA_CALIDAD = 0.72;

const HOST = 'firebasestorage.googleapis.com';

/**
 * `social/uid/1699_foto.png` → `social/uid/1699_foto.thumb.webp`.
 * Es la única definición de la convención: la usan el cliente al subir, el
 * script de relleno y `urlMiniatura()`.
 */
export function rutaMiniatura(ruta: string): string {
    return ruta.replace(/\.[^./]*$/, '') + '.thumb.webp';
}

/** `true` si la ruta ya ES una miniatura (para no generar miniaturas de miniaturas). */
export function esMiniatura(ruta: string): boolean {
    return ruta.endsWith('.thumb.webp');
}

/**
 * Devuelve la URL de la miniatura de una imagen de Firebase Storage, o `null`
 * si no se puede derivar (no es de Storage, no es una carpeta con miniatura, o
 * ya es una miniatura). Nunca lanza.
 */
export function urlMiniatura(url?: string | null): string | null {
    if (!url || typeof url !== 'string' || !url.includes(HOST)) return null;
    try {
        const corte = url.indexOf('?');
        const base = corte === -1 ? url : url.slice(0, corte);
        const cola = corte === -1 ? '' : url.slice(corte);

        // .../o/<ruta-codificada>   — la ruta va con los `/` como `%2F`.
        const marca = base.indexOf('/o/');
        if (marca === -1) return null;
        const codificada = base.slice(marca + 3);
        if (!codificada) return null;

        const carpeta = codificada.split('%2F')[0];
        if (!CARPETAS_CON_MINIATURA.includes(carpeta as any)) return null;
        if (esMiniatura(codificada)) return null;

        // La extensión tiene que estar en el ÚLTIMO segmento: si el fichero no
        // tiene extensión, el punto que encontraríamos sería el del dominio.
        const ultimo = codificada.lastIndexOf('%2F');
        if (codificada.lastIndexOf('.') <= ultimo) return null;

        return base.slice(0, marca + 3) + rutaMiniatura(codificada) + cola;
    } catch {
        return null;
    }
}
